export function enums(item) {
    if (!Array.isArray(item)) return window.app.enums[item];
    return item.reduce((val, cur) => val[cur], window.app.enums);
}

export function enumValue(item) {
    let final = null;
    if (!Array.isArray(item)) {
        final = Object.keys(window.app.enums).find((x) => window.app.enums[x] === item);
    } else {
        const lastVal = item.pop();
        const mapped = item.reduce((val, cur) => val[cur], window.app.enums);
        final = Object.keys(mapped).find((x) => mapped[x] === lastVal);
    }
    if (typeof final === 'undefined') {
        return false;
    }
    return parseInt(final);
}

export function enumsToOptions(item) {
    const itemList = enums(item);
    return Object.keys(itemList).map((x) => ({ value: parseInt(x), label: itemList[x] }));
}

function statusRollups() {
    return {
        [enumValue(['gap', 'type', 'FOC Gap'])]: {
            [enumValue(['gap', 'status', 'Addressed'])]: [
                enumValue(['gap', 'status', 'Addressed']),
                enumValue(['gap', 'status', 'Confirmed']),
                enumValue(['gap', 'status', 'Disagree/Resolved/Closed']),
                enumValue(['gap', 'status', 'Non-DataRap Covered']),
            ],
        },
        [enumValue(['gap', 'type', 'Quality Gap'])]: {
            [enumValue(['gap', 'status', 'Pending'])]: [
                enumValue(['gap', 'status', 'Pending']),
                enumValue(['gap', 'status', 'New']),
                enumValue(['gap', 'status', '']),
                enumValue(['gap', 'status', 'Fail']),
                enumValue(['gap', 'status', 'Failed Validation']),
            ],
            [enumValue(['gap', 'status', 'Addressed'])]: [
                enumValue(['gap', 'status', 'Addressed']),
                enumValue(['gap', 'status', 'Confirmed']),
            ],
            [enumValue(['gap', 'status', 'Fail'])]: [
                enumValue(['gap', 'status', 'Fail']),
                enumValue(['gap', 'status', 'Failed Validation']),
            ],
        },
        [enumValue(['gap', 'type', 'Risk Gap'])]: {
            [enumValue(['gap', 'status', 'Pending'])]: [
                enumValue(['gap', 'status', 'Pending']),
                enumValue(['gap', 'status', 'Fail']),
                enumValue(['gap', 'status', 'Failed Validation']),
                enumValue(['gap', 'status', 'Insufficient']),
            ],
            [enumValue(['gap', 'status', 'Addressed'])]: [
                enumValue(['gap', 'status', 'Addressed']),
                enumValue(['gap', 'status', 'Confirmed']),
                enumValue(['gap', 'status', 'Disagree/Resolved/Closed']),
                enumValue(['gap', 'status', 'Non-DataRap Covered']),
            ],
            [enumValue(['gap', 'status', 'Fail'])]: [
                enumValue(['gap', 'status', 'Fail']),
                enumValue(['gap', 'status', 'Insufficient']),
            ],
        },
    };
}

export function getGapStatusRollups(gapType, status) {
    return statusRollups()[gapType]?.[status] || [status];
}

export function isInRolledUpStatus(gapType, parentStatus, childStatus) {
    return getGapStatusRollups(gapType, parentStatus).indexOf(childStatus) > -1;
}

export function getGapTypeBySection(section) {
    switch (section) {
        case 'foc_gaps':
            return enumValue(['gap', 'type', 'FOC Gap']);
        case 'quality_gaps':
            return enumValue(['gap', 'type', 'Quality Gap']);
        case 'risk_gaps':
            return enumValue(['gap', 'type', 'Risk Gap']);
        default:
            return null;
    }
}

export function getRollupStatusForGapType(gapType, status) {
    const sectionRollup = statusRollups()[getGapTypeBySection(gapType)] || {};
    return parseInt(Object.keys(sectionRollup).find((key) => sectionRollup[key].includes(status)));
}

export function gapIsAddressed(gap) {
    return isInRolledUpStatus(gap.type, enumValue(['gap', 'status', 'Addressed']), gap.status);
}

export function gapIsFailed(gap) {
    return isInRolledUpStatus(gap.type, enumValue(['gap', 'status', 'Fail']), gap.status);
}

export function gapIsPending(gap) {
    return isInRolledUpStatus(gap.type, enumValue(['gap', 'status', 'Pending']), gap.status);
}

export function gapIsUnderReview(gap) {
    return isInRolledUpStatus(gap.type, enumValue(['gap', 'status', 'Under Review']), gap.status);
}

export function getGapsByCategory(gaps, status) {
    const gapsByCategory = [];
    const categories = {};

    gaps.forEach((gap) => {
        let showGap = !status || isInRolledUpStatus(gap.type, status, gap.status);
        // Quality gaps have conditions where non-pending cards show along Pending
        if (!showGap && gap.type === enumValue(['gap', 'type', 'Quality Gap'])) {
            if (
                status === enumValue(['gap', 'status', 'Pending']) &&
                gapIsAddressed(gap) &&
                gap.measure.is_manual_add_compatible
            ) {
                showGap =
                    gap.submission_answer?.status === enumValue(['submission', 'status', 'New']);
            }
            if (
                status === enumValue(['gap', 'status', 'Addressed']) &&
                isInRolledUpStatus(
                    gap.type,
                    enumValue(['gap', 'status', 'Addressed']),
                    gap.system_status
                ) &&
                gapIsUnderReview(gap)
            ) {
                showGap = true;
            }
        }
        if (showGap) {
            const measureCategoryName =
                gap.measure.measure_category?.description || 'No Measure Category';
            // Handle gap categories we haven't seen
            if (typeof categories[measureCategoryName] === 'undefined') {
                categories[measureCategoryName] = gapsByCategory.length;
                gapsByCategory.push({
                    name: measureCategoryName,
                    measure_category: gap.measure.measure_category,
                    gaps: [gap],
                });
            } else {
                gapsByCategory[categories[measureCategoryName]].gaps.push(gap);
            }
        }
    });

    // Sort categories
    gapsByCategory.sort((cat1, cat2) => {
        return cat1.measure_category.sequence - cat2.measure_category.sequence;
    });

    // Sort gaps
    gapsByCategory.forEach((cat) => {
        cat.gaps.sort((gap1, gap2) => {
            return gap1.measure.sequence - gap2.measure.sequence;
        });
    });

    return gapsByCategory;
}

export function gapShouldShowControl(selectedStatus, gapType) {
    const selectedStatuses = [
        enumValue(['gap', 'status', 'Pending']),
        enumValue(['gap', 'status', 'Fail']),
    ];

    if (typeof gapType !== 'undefined') {
        switch (gapType) {
            case enumValue(['gap', 'type', 'Quality Gap']):
                selectedStatuses.push(enumValue(['gap', 'status', 'Addressed']));
                break;
            default:
                break;
        }
    }

    /* Only show Add Condition button, Date of Visit picker, Submit button,
     and Attachments button when viewing Pending or Failed Risk Gaps */
    return selectedStatuses.includes(selectedStatus);
}
